import { Link } from "react-router-dom";
import EnquiryForm from "../components/common/EnquiryForm";
import InstagramBanner from "../components/common/InstagramBanner";

const steps = [
  {
    icon: "/static/images/icon-process-location.svg",
    title: "Location",
    text: "If you haven't seen your local Artistic Spaces work place already, take a look on the website for your nearest site. There is sure to be one close to you and we are expanding with new sites becoming available all the time.",
  },
  {
    icon: "/static/images/icon-process-features.svg",
    title: "Focus",
    text: "With 19 locations across London, finding a workspace that suits you is easy. Get in touch to let us know what you need, or check our website for current availability. We're always growing, so new spaces are regularly becoming available.",
  },
  {
    icon: "/static/images/icon-process-viewing.svg",
    title: "Join us",
    text: "Once you've booked a viewing, we'll meet you on-site to show you the space, as well as the rest of the building and its facilities. If you find the right fit, we can move quickly — our contracts and documents can sometimes be completed within 24 hours.",
  },
];

const features = [
  "24/7 Access – Many of our buildings offer round-the-clock access, so you can work on your own schedule.",
  "High-Speed Wi-Fi – Stay connected with reliable internet across all our spaces.",
  "Registered Business Address – Use your dedicated workspace as your official business address.",
  "Communal Facilities – Enjoy access to tea points, washrooms, and even showers in select buildings.",
  "Secure Workspaces – CCTV-monitored premises ensure the safety of your business.",
  "Flexibility to Adapt – Need to customise your space? We provide the freedom to make practical changes so your workspace fits your business needs.",
  "Dedicated Support – Our customer care team is available via phone and online, with rapid maintenance response when needed.",
];

export default function HowItWorks() {
  return (
    <main className="pt-16 md:pt-20">
      {/* Hero */}
      <section
        className="relative w-full h-64 md:h-80"
        style={{
          backgroundImage: "url('/static/images/hero-howworks.webp')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-black/40" />
      </section>

      {/* Intro */}
      <section className="w-full bg-[#f5f4f0]">
        <div className="flex flex-col md:flex-row min-h-[400px]">
          <div className="w-full md:w-1/2 flex items-center px-8 md:px-16 lg:px-24 py-12">
            <div className="max-w-sm">
              <h1 className="text-3xl md:text-4xl font-semibold text-gray-900 mb-5">How It Works</h1>
              <p className="text-gray-600 leading-relaxed text-sm md:text-base">
                Across each of our studio locations, we have a large range of different style spaces to accommodate
                creative and logistical requirements. Whether you are working with paint and clay and need an
                individual sink or run e-commerce operations and need a secure 5G connection.
              </p>
            </div>
          </div>
          <div className="w-full md:w-1/2 min-h-[280px] md:min-h-[400px] overflow-hidden">
            <img
              src="/static/images/howitworks-intro-pic.webp"
              alt="How it works"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Simplicity */}
      <section className="w-full bg-white">
        <div className="flex flex-col md:flex-row-reverse min-h-[400px]">
          <div className="w-full md:w-1/2 flex items-center px-8 md:px-16 lg:px-24 py-12">
            <div className="max-w-sm">
              <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 mb-5">Simplicity</h2>
              <p className="text-gray-600 leading-relaxed text-sm md:text-base">
                Our growing network of studios across London has been the starting place for many successful
                ventures and continues to be the home of established artists, designers and entrepreneurs. Over the
                years we've built on experience and developed all our sites for the core purpose of productivity,
                creativity and affordability.
              </p>
            </div>
          </div>
          <div className="w-full md:w-1/2 min-h-[280px] md:min-h-[400px] overflow-hidden">
            <img
              src="/static/images/howitworks-pic1.webp"
              alt="Simplicity"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Flexible */}
      <section className="w-full bg-[#f5f4f0]">
        <div className="flex flex-col md:flex-row min-h-[400px]">
          <div className="w-full md:w-1/2 flex items-center px-8 md:px-16 lg:px-24 py-12">
            <div className="max-w-sm">
              <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 mb-5">Flexible</h2>
              <p className="text-gray-600 leading-relaxed text-sm md:text-base">
                Split across nineteen creative complexes in London, our studios provide secure access, CCTV, onsite
                facilities, and tea points. Most of our studios even offer 24-hour access, ensuring flexibility for
                your work schedule. Our simple contract terms give you the freedom to stay long-term or leave with
                much shorter notice than most commercial leases.
              </p>
            </div>
          </div>
          <div className="w-full md:w-1/2 min-h-[280px] md:min-h-[400px] overflow-hidden">
            <img
              src="/static/images/howitworks-pic2.webp"
              alt="Flexible workspace"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* The Process */}
      <section className="w-full bg-white px-8 md:px-16 lg:px-24 py-16">
        <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 mb-12">The process</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {steps.map((step) => (
            <div key={step.title} className="flex flex-col gap-4">
              <img src={step.icon} alt={step.title} className="w-12 h-12" />
              <h3 className="text-lg font-semibold text-gray-900">{step.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{step.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Affordable */}
      <section className="w-full bg-[#f5f4f0]">
        <div className="flex flex-col md:flex-row-reverse min-h-[400px]">
          <div className="w-full md:w-1/2 flex items-center px-8 md:px-16 lg:px-24 py-12">
            <div className="max-w-sm">
              <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 mb-5">Affordable</h2>
              <p className="text-gray-600 leading-relaxed text-sm md:text-base">
                At Artistic Spaces, we believe in fair and transparent pricing with no hidden fees. Each workspace
                is individually priced based on its size, location, and features, rather than a standard square
                footage rate, ensuring a more tailored approach. For every space you consider, we will clearly
                outline the rent and service charge before your viewing, so you always know exactly what you're
                paying.
              </p>
            </div>
          </div>
          <div className="w-full md:w-1/2 min-h-[280px] md:min-h-[400px] overflow-hidden">
            <img
              src="/static/images/howitworks-pic3.webp"
              alt="Affordable"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* What you get */}
      <section className="w-full bg-white">
        <div className="flex flex-col md:flex-row min-h-[400px]">
          <div className="w-full md:w-1/2 flex items-center px-8 md:px-16 lg:px-24 py-12">
            <div className="max-w-sm">
              <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 mb-6">What you get*</h2>
              <ul className="flex flex-col gap-3">
                {features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3 text-sm text-gray-600">
                    <span className="text-[#8a7d5a] font-bold mt-0.5 flex-shrink-0">—</span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="w-full md:w-1/2 min-h-[280px] md:min-h-[400px] overflow-hidden">
            <img
              src="/static/images/howitworks-pic4.webp"
              alt="What you get"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      <InstagramBanner />
      <EnquiryForm pageUri="https://artistic-spaces.co.uk/how-it-works" pageName="How It Works" />
    </main>
  );
}